/** @format */
import {
    SIZE_CONSTRAINTS,
    PRODUCT_TYPE_LABEL,
    SHAPE_CARTON_AUTOLOCK,
    SHAPE_CARTON_STRAIGHT_TUCK,
    PRODUCT_TYPE_CARTON,
    SHAPE_CARTON_CUSTOM
} from 'frontier-constants';

/**
 * Checks if a given value is a multiple of the specified step size.
 * This function is useful in scenarios where you want to ensure that a value aligns with a certain step size.
 * For example, in a grid system, you might want to ensure that all values are multiples of 8 (a common step size in design systems).
 *
 * @param {number} value - The value to check.
 * @param {number} [step=8] - The step size to use. Defaults to 8.
 * @param {number} [decimalScale=3] - The scale to use when checking the value. Defaults to 3. This is used to handle floating point precision issues.
 * @returns {boolean} - Returns true if the value is a multiple of the step size, false otherwise.
 */
const isValueMultipleOfStep = (value, step = 8, decimalScale = 3) => {
    // Calculate the remainder when 1000 is divided by the step size.
    // This is used to scale the value up to avoid issues with floating point precision.
    const remainder = 1000 / step;

    // Scale the value up by the decimal scale (default is 1000), then check if it's a multiple of the remainder.
    // If it is, that means the original value is a multiple of the step size.
    return (value * Math.pow(10, decimalScale)) % remainder === 0;
};

class DimensionValidator {
    constructor({
        width = 0,
        length = 0,
        depth = 0,
        shapeId = SHAPE_CARTON_AUTOLOCK,
        productTypeId = 0,
        step = 8,
        decimalScale = 3
    }) {
        this.width = width;
        this.length = length;
        this.depth = depth;
        this.shapeId = shapeId > 0 ? parseInt(shapeId) : SHAPE_CARTON_AUTOLOCK;
        this.productTypeId = parseInt(productTypeId);
        this.step = step;
        this.decimalScale = decimalScale;
    }

    setDecimalScale = decimalScale => {
        this.decimalScale = decimalScale;
    };

    setStep = step => {
        this.step = step;
    };

    isBelowMinWidth = () => {
        return this.width && parseFloat(this.width) < SIZE_CONSTRAINTS[this.shapeId].min.width;
    };

    belowMinWidthMessage = () => {
        return `Must be at least ${SIZE_CONSTRAINTS[this.shapeId].min.width}"`;
    };

    isAboveMaxWidth = () => {
        return parseFloat(this.width) > SIZE_CONSTRAINTS[this.shapeId].max.width;
    };

    aboveMaxWidthMessage = () => {
        return `Cannot exceed ${SIZE_CONSTRAINTS[this.shapeId].max.width}"`;
    };

    isBelowMinLength = () => {
        return this.length && parseFloat(this.length) < SIZE_CONSTRAINTS[this.shapeId].min.length;
    };

    belowMinLengthMessage = () => {
        return `Must be at least ${SIZE_CONSTRAINTS[this.shapeId].min.length}"`;
    };

    isAboveMaxLength = () => {
        return parseFloat(this.length) > SIZE_CONSTRAINTS[this.shapeId].max.length;
    };

    aboveMaxLengthMessage = () => {
        return `Cannot exceed ${SIZE_CONSTRAINTS[this.shapeId].max.length}"`;
    };

    isValueMultipleOfStep = value => {
        return isValueMultipleOfStep(value, this.step, this.decimalScale);
    };

    isSnapped = () => {
        if (this.width) {
            return this.isSnappedValue(this.width);
        }
        if (this.length) {
            return this.isSnappedValue(this.length);
        }
        if (this.depth) {
            return this.isSnappedValue(this.depth);
        }
    };

    isSnappedValue = value => {
        const isSnapped = this.isValueMultipleOfStep(value);
        if (!isSnapped) {
            const closest = (Math.round(value * this.step) / this.step).toFixed(this.decimalScale);
            return `Please round to the nearest 1/${this.step} (${closest})`;
        }
        return false;
    };

    isNotSnapped = value => {
        return this.isSnappedValue(value) !== false;
    };

    notSnappedMessage = value => {
        const closest = (Math.round(value * this.step) / this.step).toFixed(this.decimalScale);
        return `Round to nearest 1/${this.step}" (${closest})`;
    };

    isBelowMinDepth = () => {
        const lowLimit =
            parseFloat(this.length) < 1.5 ||
            parseFloat(this.width) < 1.5 ||
            parseInt(this.shapeId) === SHAPE_CARTON_STRAIGHT_TUCK
                ? SIZE_CONSTRAINTS[SHAPE_CARTON_STRAIGHT_TUCK].min.depth
                : SIZE_CONSTRAINTS[SHAPE_CARTON_AUTOLOCK].min.depth;
        return parseFloat(this.depth) < lowLimit;
    };

    belowMinDepthMessage = () => {
        const lowLimit =
            parseFloat(this.length) < 1.5 ||
            parseFloat(this.width) < 1.5 ||
            parseInt(this.shapeId) === SHAPE_CARTON_STRAIGHT_TUCK
                ? SIZE_CONSTRAINTS[SHAPE_CARTON_STRAIGHT_TUCK].min.depth
                : SIZE_CONSTRAINTS[SHAPE_CARTON_AUTOLOCK].min.depth;
        if (parseInt(this.shapeId) === SHAPE_CARTON_AUTOLOCK) {
            return `Must be at least ${lowLimit}", for Autolock`;
        }

        return `Must be at least ${lowLimit}"`;
    };

    isAboveMaxDepth = () => {
        return parseFloat(this.depth) > SIZE_CONSTRAINTS[SHAPE_CARTON_AUTOLOCK].max.depth;
    };

    aboveMaxDepthMessage = () => {
        return `Cannot exceed ${SIZE_CONSTRAINTS[SHAPE_CARTON_AUTOLOCK].max.depth}"`;
    };

    isBelowMinWidthCarton = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].min.width;
        return parseFloat(this.width) < limit;
    };

    belowMinWidthCartonMessage = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].min.width;
        if (this.shapeId === SHAPE_CARTON_AUTOLOCK) {
            return `Must be at least ${limit}", Use Straight Tuck`;
        }
        return `Must be at least ${limit}"`;
    };

    isBelowMinWidthAndLength = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].min.width_length;
        return parseFloat(this.width) + parseFloat(this.length) < limit;
    };

    belowMinWidthAndLengthMessage = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].min.width_length;
        return `Width and length combined must be at least ${limit}"`;
    };

    isAboveMaxWidthAndLength = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].max.width_length;
        return parseFloat(this.width) + parseFloat(this.length) > limit;
    };

    aboveMaxWidthAndLengthMessage = () => {
        const limit = SIZE_CONSTRAINTS[this.shapeId].max.width_length;
        return `Width and length combined cannot exceed ${limit}"`;
    };

    isWidthGreaterThanLength = () => {
        return this.width > this.length;
    };

    widthGreaterThanLengthMessage = () => {
        return `Width must be less than or equal to length`;
    };

    isValidSize = () => {
        if (this.productTypeId === PRODUCT_TYPE_CARTON && this.shapeId === SHAPE_CARTON_CUSTOM) {
            return true; // Custom cartons don't have size constraints
        } else if (this.productTypeId === PRODUCT_TYPE_LABEL) {
            if (
                this.step &&
                this.decimalScale &&
                (this.isNotSnapped(this.width) || this.isNotSnapped(this.length))
            ) {
                return false;
            }
            if (
                this.isBelowMinWidth() ||
                this.isAboveMaxWidth() ||
                this.isBelowMinLength() ||
                this.isAboveMaxLength()
            ) {
                return false;
            }
            return true;
        } else {
            const checks = {
                isWidthGreaterThanLength: this.isWidthGreaterThanLength(),
                isBelowMinDepth: this.isBelowMinDepth(),
                isAboveMaxDepth: this.isAboveMaxDepth(),
                isBelowMinWidthCarton: this.isBelowMinWidthCarton(),
                isBelowMinWidthAndLength: this.isBelowMinWidthAndLength(),
                isAboveMaxWidthAndLength: this.isAboveMaxWidthAndLength(),
                isNotSnappedWidth: this.isNotSnapped(this.width),
                isNotSnappedLength: this.isNotSnapped(this.length),
                isNotSnappedDepth: this.isNotSnapped(this.depth)
            };

            if (Object.values(checks).filter(c => c === true).length > 0) {
                return false;
            }
            return true;
        }
    };

    getErrors = () => {
        let errors = [];
        let fields = [];
        if (this.productTypeId === PRODUCT_TYPE_CARTON && this.shapeId === SHAPE_CARTON_CUSTOM) {
            // Custom cartons don't have size constraints
        } else if (this.productTypeId === PRODUCT_TYPE_LABEL) {
            fields = [
                {
                    value: this.width,
                    checks: [this.isBelowMinWidth, this.isAboveMaxWidth, this.isNotSnapped],
                    messages: [
                        this.belowMinWidthMessage,
                        this.aboveMaxWidthMessage,
                        this.notSnappedMessage
                    ],
                    label: 'Width'
                },
                {
                    value: this.length,
                    checks: [this.isBelowMinLength, this.isAboveMaxLength, this.isNotSnapped],
                    messages: [
                        this.belowMinLengthMessage,
                        this.aboveMaxLengthMessage,
                        this.notSnappedMessage
                    ],
                    label: 'Length'
                }
            ];
        } else {
            fields = [
                {
                    value: this.width,
                    checks: [this.isBelowMinWidthCarton, this.isAboveMaxWidth, this.isNotSnapped],
                    messages: [
                        this.belowMinWidthCartonMessage,
                        this.aboveMaxWidthMessage,
                        this.notSnappedMessage
                    ],
                    label: 'Width'
                },
                {
                    value: this.length,
                    checks: [this.isBelowMinLength, this.isAboveMaxLength, this.isNotSnapped],
                    messages: [
                        this.belowMinLengthMessage,
                        this.aboveMaxLengthMessage,
                        this.notSnappedMessage
                    ],
                    label: 'Length'
                },
                {
                    value: this.depth,
                    checks: [this.isBelowMinDepth, this.isAboveMaxDepth, this.isNotSnapped],
                    messages: [
                        this.belowMinDepthMessage,
                        this.aboveMaxDepthMessage,
                        this.notSnappedMessage
                    ],
                    label: 'Depth'
                },
                {
                    value: {width: this.width, length: this.length},
                    checks: [
                        this.isBelowMinWidthAndLength,
                        this.isAboveMaxWidthAndLength,
                        this.isWidthGreaterThanLength
                    ],
                    messages: [
                        this.belowMinWidthAndLengthMessage,
                        this.aboveMaxWidthAndLengthMessage,
                        this.widthGreaterThanLengthMessage
                    ],
                    label: ''
                }
            ];
        }

        errors = fields.reduce((errors, {value, checks, messages, label}) => {
            for (let i = 0; i < checks.length; i++) {
                if (checks[i](value)) {
                    errors.push(`${label} ${messages[i]()}`);
                }
            }
            return errors;
        }, []);

        return errors;
    };
}

export default DimensionValidator;

export {isValueMultipleOfStep};
